import {ContactRelationship, School, SchoolClassInvitation} from "models"
import React, {memo, useMemo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component"
import ApiMakerConfig from "@kaspernj/api-maker/build/config"
import AppHistory from "shared/history"
import ChalkyLayout from "components/chalky-layout"
import {digg} from "diggerize"
import ProUpgradeOverlay from "components/pro/upgrade-overlay"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import useAnythingPro from "components/subscriptions/use-anything-pro"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useI18n from "i18n-on-steroids/src/use-i18n"
import useQueryParams from "on-location-changed/build/use-query-params"
import UsersTable from "./my-users/users-table"
import {View} from "react-native"

export default memo(shapeComponent(class RoutesUserMyUsersRoute extends ShapeComponent {
  setup() {
    setLayout("user")

    const {anythingPro} = useAnythingPro()
    const {t} = useI18n({namespace: "js.routes.user.my_users"})
    const currentUser = useCurrentUser()
    const queryParams = useQueryParams()

    this.setInstance({anythingPro, currentUser, t})
    this.useStates({
      schools: null,
      schoolRolesPerSchool: null,
      schoolClassesForSchoolsAndPeople: null,
      schoolClassInvitationsByUserId: null
    })

    this.params = useMemo(() => {
      if (queryParams.my_users_params) {
        return JSON.parse(queryParams.my_users_params)
      } else {
        return {}
      }
    }, [queryParams.my_users_params])

    useMemo(() => {
      if (currentUser && anythingPro) {
        this.loadSchools()
      } else {
        this.setState({schools: null})
      }
    }, [anythingPro, currentUser?.id()])
  }

  async loadSchools() {
    const {currentUser} = this.tt
    const schools = await School
      .select({
        School: ["contactId", "id", "name"]
      })
      .ransack({with_contact_id: currentUser.contactId()})
      .groupBy("id")
      .toArray()

    const schoolContactIds = []
    const schoolRolesPerSchool = {}

    for (const school of schools) {
      schoolContactIds.push(school.contactId())
      schoolRolesPerSchool[school.contactId()] = {}
    }

    this.loadSchoolInvitations(schools)

    const allSchoolRoles = await ContactRelationship
      .ransack({
        parent_id_eq_any: schoolContactIds,
        where_relationship_type: ["admin", "school_class", "school_leader", "teacher"]
      })
      .preload(["child.user", "contact_relationship_type"])
      .select({
        Contact: ["email", "id", "name"],
        ContactRelationship: ["childId", "id", "parentId"],
        ContactRelationshipType: ["identifier", "name"],
        User: ["id", "lastActivityAt", "name"]
      })
      .abilities({
        ContactRelationship: ["destroy", "edit"]
      })
      .toArray()

    const schoolRoles = []
    const schoolClassIds = []
    const schoolIdsForSchoolClases = {}

    for (const schoolRole of allSchoolRoles) {
      if (schoolRole.contactRelationshipType().identifier() == "school_class") {
        schoolIdsForSchoolClases[schoolRole.childId()] = schoolRole.parentId()
        schoolClassIds.push(schoolRole.childId())
      } else {
        schoolRoles.push(schoolRole)
      }
    }

    const schoolClassRoles = await ContactRelationship
      .ransack({
        parent_id_eq_any: schoolClassIds,
        where_relationship_type: ["teacher"]
      })
      .toArray()

    const schoolClassesForSchoolsAndPeople = {}

    for (const schoolClassRole of schoolClassRoles) {
      const schoolContactId = digg(schoolIdsForSchoolClases, schoolClassRole.parentId())

      if (!(schoolContactId in schoolClassesForSchoolsAndPeople)) {
        schoolClassesForSchoolsAndPeople[schoolContactId] = {}
      }

      if (!(schoolClassRole.childId() in schoolClassesForSchoolsAndPeople[schoolContactId])) {
        schoolClassesForSchoolsAndPeople[schoolContactId][schoolClassRole.childId()] = []
      }

      schoolClassesForSchoolsAndPeople[schoolContactId][schoolClassRole.childId()].push(schoolClassRole)
    }

    for (const schoolRole of schoolRoles) {
      const schoolRolesForSchool = schoolRolesPerSchool[schoolRole.parentId()]

      if (!(schoolRole.childId() in schoolRolesForSchool)) {
        schoolRolesForSchool[schoolRole.childId()] = {
          contact: schoolRole.child(),
          schoolRoles: []
        }
      }

      schoolRolesForSchool[schoolRole.childId()].schoolRoles.push(schoolRole)
    }

    this.setState({schools, schoolRolesPerSchool, schoolClassesForSchoolsAndPeople})
  }

  async loadSchoolInvitations(schools) {
    const schoolIds = schools.map((school) => school.id())
    const schoolClassInvitations = await SchoolClassInvitation
      .ransack({
        resource_type_eq: "School",
        resource_id_eq_any: schoolIds
      })
      .select({
        SchoolClassInvitation: ["createdUserId", "existingUserId", "state"]
      })
      .toArray()

    const schoolClassInvitationsByUserId = {}

    for (const schoolClassInvitation of schoolClassInvitations) {
      const userId = schoolClassInvitation.createdUserId() || schoolClassInvitation.existingUserId()

      schoolClassInvitationsByUserId[userId] = schoolClassInvitation
    }

    this.setState({schoolClassInvitationsByUserId})
  }

  render() {
    const {anythingPro, t} = this.tt
    const {schools, schoolClassesForSchoolsAndPeople, schoolClassInvitationsByUserId, schoolRolesPerSchool} = this.s

    return (
      <ChalkyLayout active="my-users" headerTitle={t(".my_users")} userMenu={false} weekBar={false}>
        {anythingPro === false &&
          <ProUpgradeOverlay onRequestClose={this.tt.onUpgradeOverlayRequestClose} />
        }
        {anythingPro === true && schoolClassInvitationsByUserId !== null && schools?.map((school) =>
          <View
            dataSet={this.cache("usersTableViewDataSet", {testid: "my-users-users-table", schoolId: school.id()}, [school.id()])}
            key={school.id()}
            style={this.usersTableViewStyle ||= {marginBottom: 30}}
          >
            <UsersTable
              params={this.params[school.id()]}
              school={school}
              schoolClassInvitationsByUserId={schoolClassInvitationsByUserId}
              schoolClassesForSchoolsAndPeople={schoolClassesForSchoolsAndPeople?.[school.contactId()] || {}}
              schoolRoles={schoolRolesPerSchool?.[school.contactId()] || {}}
            />
          </View>
        )}
      </ChalkyLayout>
    )
  }

  onUpgradeOverlayRequestClose = ({reason} = {}) => {
    if (reason !== "stay-basic") {
      return
    }

    if (this.tt.anythingPro === false) {
      this.goBackOrDashboard()
    }
  }

  goBackOrDashboard() {
    const history = ApiMakerConfig.getHistory()

    if (history?.back) {
      history.back()

      return
    }

    if (history?.goBack) {
      history.goBack()

      return
    }

    if (history?.go && typeof history.length === "number" && history.length > 1) {
      history.go(-1)

      return
    }

    if (typeof window !== "undefined" && window.history?.length > 1) {
      window.history.back()

      return
    }

    AppHistory.push(Routes.userRootPath())
  }
}))
